
import { useTranslation } from 'react-i18next';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const AboutPage = () => {
  const { t } = useTranslation('common');

  return (
    <div className="min-h-screen">
      <Header />
      <main>
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-blue-50 to-indigo-100 py-20">
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-20"
            style={{
              backgroundImage: 'url(https://public.readdy.ai/ai/img_res/276e79b70676b0053204f81dfb8bfabe.jpg)'
            }}
          />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              About Alpbuild
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Your trusted partner for professional construction and renovation services in Switzerland. Quality craftsmanship with Swiss precision since our founding.
            </p>
          </div>
        </section>

        {/* Company Story */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                  Our Story
                </h2>
                <p className="text-lg text-gray-600 mb-6">
                  Alpbuild was founded with a simple mission: to provide exceptional construction and renovation services that meet the highest Swiss quality standards. Our team of experienced craftsmen combines traditional techniques with modern innovation to deliver outstanding results.
                </p>
                <p className="text-lg text-gray-600 mb-6">
                  Operating primarily in the cantons of Vaud and Geneva, we have built a reputation for reliability, precision, and customer satisfaction. Every project, whether residential or commercial, receives our full attention to detail and commitment to excellence.
                </p>
                <div className="grid grid-cols-2 gap-6">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-blue-600 mb-2">80+</div>
                    <div className="text-gray-600">Projects Completed</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-blue-600 mb-2">21+</div>
                    <div className="text-gray-600">Years Experience</div>
                  </div>
                </div>
              </div>
              <div className="relative">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20Swiss%20construction%20team%20working%20on%20high%20quality%20renovation%20project%2C%20modern%20tools%20and%20equipment%2C%20clean%20organized%20workspace%2C%20attention%20to%20detail%20and%20craftsmanship%2C%20bright%20natural%20lighting&width=600&height=500&seq=about-story&orientation=landscape"
                  alt="Our team at work"
                  className="rounded-2xl shadow-lg object-cover object-top w-full h-96"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Our Values
              </h2>
              <p className="text-lg text-gray-600">
                The principles that guide everything we do
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-award-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Quality Excellence</h3>
                <p className="text-gray-600">
                  We never compromise on quality. Every project is executed with Swiss precision and attention to detail, using only the finest materials and proven techniques.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-handshake-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Trust & Reliability</h3>
                <p className="text-gray-600">
                  Building lasting relationships with our clients through honest communication, transparent pricing, and reliable service delivery on every project.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-lightbulb-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Innovation</h3>
                <p className="text-gray-600">
                  Continuously improving our methods and embracing new technologies while respecting traditional craftsmanship and time-tested techniques.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Team */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Our Expert Team
              </h2>
              <p className="text-lg text-gray-600">
                Skilled professionals dedicated to delivering exceptional results
              </p>
            </div>

            {/* Team members section - currently empty, ready for real team members */}
            <div className="text-center text-gray-500">
              <p className="text-lg">Team members will be added soon</p>
            </div>
          </div>
        </section>

        {/* Certifications */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Certifications & Standards
              </h2>
              <p className="text-lg text-gray-600">
                Committed to the highest industry standards and regulations
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-shield-check-line text-2xl text-white"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Swiss Quality</h3>
                <p className="text-gray-600">Certified Swiss construction standards</p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-leaf-line text-2xl text-white"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Eco-Friendly</h3>
                <p className="text-gray-600">Sustainable building practices</p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-safety-certificate-line text-2xl text-white"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Safety First</h3>
                <p className="text-gray-600">Comprehensive safety protocols</p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-insurance-line text-2xl text-white"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Insured</h3>
                <p className="text-gray-600">Full liability and project insurance</p>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default AboutPage;
